import { useEffect, useState } from 'react';
import { useDebouncedValue } from './useDebouncedValue';

/** Задержка записи ширины в настройки: панель отдаёт ширину на каждый пиксель перетаскивания */
const PERSIST_DELAY_MS = 500;

/** Ширина панели управления колонками и её обработчик для DataGrid */
type Result = {
  /** Текущая ширина панели в px; `undefined` — сохранённой ширины нет */
  width: number | undefined;
  /** Обработчик перетаскивания ручки — вызывается панелью на каждый пиксель */
  onWidthChange: (next: number) => void;
};

/**
 * Ширина панели управления колонками: мгновенное состояние для перетаскивания ручки
 * и отложенная запись в настройки.
 *
 * Панель контролируемая — ширину нужно возвращать ей на каждый пиксель перетаскивания,
 * поэтому выставленная ширина живёт в локальном состоянии, а в настройки (и оттуда на бэкенд)
 * уходит с задержкой. Когда записанная ширина вернулась из настроек, панель снова следует им:
 * ширину, общую для нескольких таблиц, сменённую у одной из них, сразу подхватывают и остальные.
 *
 * @param savedWidth - Сохранённая ширина из настроек; приходит асинхронно, после авторизации
 * @param onPersist - Запись ширины в настройки; ссылка должна быть стабильной
 */
export const useColumnsPanelWidth = (savedWidth: number | undefined, onPersist: (width: number) => void): Result => {
  // Ширина, выставленная перетаскиванием и ещё не дошедшая до настроек
  const [draftWidth, setDraftWidth] = useState<number | undefined>();
  const debouncedWidth = useDebouncedValue(draftWidth, PERSIST_DELAY_MS);

  useEffect(() => {
    if (debouncedWidth === undefined || debouncedWidth === savedWidth) return;
    onPersist(debouncedWidth);
  }, [debouncedWidth, savedWidth, onPersist]);

  useEffect(() => {
    if (draftWidth !== undefined && draftWidth === savedWidth) setDraftWidth(undefined);
  }, [draftWidth, savedWidth]);

  return { width: draftWidth ?? savedWidth, onWidthChange: setDraftWidth };
};
